import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Event-driven bridge for imperative appConfirm(...) and appPrompt(...) calls anywhere in the application.
 */
let confirmHandler = null;
let promptHandler = null;

export function appConfirm({
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'primary' | 'info'
  icon = null,
} = {}) {
  return new Promise((resolve) => {
    if (confirmHandler) {
      confirmHandler({
        title,
        message,
        confirmText,
        cancelText,
        variant,
        icon,
        resolve,
      });
    } else {
      // Fallback in case component isn't mounted yet
      // eslint-disable-next-line no-restricted-globals
      const ok = window.confirm(`${title}\n\n${message}`);
      resolve(ok);
    }
  });
}

export function appPrompt({
  title = 'Input Required',
  message = '',
  defaultValue = '',
  placeholder = '',
  confirmText = 'OK',
  cancelText = 'Cancel',
  inputType = 'text',
  validate = null,
} = {}) {
  return new Promise((resolve) => {
    if (promptHandler) {
      promptHandler({
        title,
        message,
        defaultValue,
        placeholder,
        confirmText,
        cancelText,
        inputType,
        validate,
        resolve,
      });
    } else {
      // Fallback in case component isn't mounted yet
      // eslint-disable-next-line no-restricted-globals
      const res = window.prompt(`${title}\n\n${message}`, defaultValue);
      resolve(res);
    }
  });
}

/**
 * Root mounted component that renders the active confirmation or prompt modal.
 * Mount once in App.jsx (e.g. next to Sonner <Toaster />).
 */
export function AppConfirmModalRoot() {
  const [confirmState, setConfirmState] = useState(null);
  const [promptState, setPromptState] = useState(null);

  useEffect(() => {
    confirmHandler = (config) => {
      setConfirmState(config);
    };
    promptHandler = (config) => {
      setPromptState(config);
    };
    return () => {
      confirmHandler = null;
      promptHandler = null;
    };
  }, []);

  const handleConfirmClose = useCallback(
    (result) => {
      if (confirmState?.resolve) {
        confirmState.resolve(result);
      }
      setConfirmState(null);
    },
    [confirmState]
  );

  const handlePromptClose = useCallback(
    (result) => {
      if (promptState?.resolve) {
        promptState.resolve(result);
      }
      setPromptState(null);
    },
    [promptState]
  );

  return (
    <>
      {confirmState && (
        <AppConfirmModal
          isOpen={Boolean(confirmState)}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          variant={confirmState.variant}
          icon={confirmState.icon}
          onConfirm={() => handleConfirmClose(true)}
          onCancel={() => handleConfirmClose(false)}
        />
      )}
      {promptState && (
        <AppPromptModal
          isOpen={Boolean(promptState)}
          title={promptState.title}
          message={promptState.message}
          defaultValue={promptState.defaultValue}
          placeholder={promptState.placeholder}
          confirmText={promptState.confirmText}
          cancelText={promptState.cancelText}
          inputType={promptState.inputType}
          validate={promptState.validate}
          onConfirm={(val) => handlePromptClose(val)}
          onCancel={() => handlePromptClose(null)}
        />
      )}
    </>
  );
}

/**
 * Reusable presentational Confirm Modal component.
 */
export default function AppConfirmModal({
  isOpen = false,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'primary' | 'info'
  icon = null,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  // Icon configuration based on variant
  let iconName = icon;
  let iconBadgeClass = 'bg-primary/10 text-primary';
  let confirmBtnClass = 'bg-primary hover:bg-primary/90 text-on-primary';

  if (variant === 'danger') {
    iconName = iconName || 'delete';
    iconBadgeClass = 'bg-error/10 text-error';
    confirmBtnClass = 'bg-error hover:bg-error/90 text-white';
  } else if (variant === 'warning') {
    iconName = iconName || 'archive';
    iconBadgeClass = 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    confirmBtnClass = 'bg-amber-600 hover:bg-amber-700 text-white';
  } else if (variant === 'primary' || variant === 'info') {
    iconName = iconName || 'help';
    iconBadgeClass = 'bg-primary/10 text-primary';
    confirmBtnClass = 'bg-primary hover:bg-primary/90 text-on-primary';
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-md rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Icon & Title */}
        <div className="flex items-start gap-4">
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${iconBadgeClass}`}
          >
            <span className="material-symbols-outlined text-2xl">{iconName}</span>
          </div>
          <div className="space-y-1.5 flex-1 pt-0.5">
            <h3 className="font-display text-lg font-bold leading-tight text-on-surface">
              {title}
            </h3>
            {message && (
              <p className="text-xs font-medium leading-relaxed text-on-surface-variant">
                {message}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer select-none"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-xl px-5 py-2.5 text-xs font-bold shadow-xs transition cursor-pointer select-none ${confirmBtnClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Reusable presentational Prompt Modal component.
 */
export function AppPromptModal({
  isOpen = false,
  title = 'Input Required',
  message = '',
  defaultValue = '',
  placeholder = '',
  confirmText = 'OK',
  cancelText = 'Cancel',
  inputType = 'text',
  validate = null,
  onConfirm,
  onCancel,
}) {
  const [val, setVal] = useState(defaultValue || '');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setVal(defaultValue || '');
    setError('');
  }, [defaultValue, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate) {
      const validationError = validate(val);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    onConfirm?.(val);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-md rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          {/* Header Icon & Title */}
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-2xl">edit_note</span>
            </div>
            <div className="space-y-1.5 flex-1 pt-0.5">
              <h3 className="font-display text-lg font-bold leading-tight text-on-surface">
                {title}
              </h3>
              {message && (
                <p className="text-xs font-medium leading-relaxed text-on-surface-variant">
                  {message}
                </p>
              )}
            </div>
          </div>

          {/* Text Input */}
          <div className="mt-4">
            <input
              ref={inputRef}
              type={inputType}
              value={val}
              onChange={(e) => {
                setVal(e.target.value);
                if (error) setError('');
              }}
              placeholder={placeholder}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/40 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition"
            />
            {error && (
              <p className="mt-1.5 text-xs font-semibold text-error">{error}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer select-none"
            >
              {cancelText}
            </button>
            <button
              type="submit"
              className="rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary hover:bg-primary/90 shadow-xs transition cursor-pointer select-none"
            >
              {confirmText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
